import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type PartnerName =
    | 'Northgate Provisions'
    | 'Halden & Rowe'
    | 'Kestrel Home'
    | 'Volta Cycles'
    | 'Brightwater Foods'
    | 'Ostrava Steel'
    | 'Pinecrest Outdoor'
    | 'Lumen Appliances'
    | 'Marlow Textiles'
    | 'Tandem Bikes'

interface PartnerWordmarkProps {
    name: PartnerName
    className?: string
}

/**
 * Invented customer wordmarks set purely in type. Sizes are in em, so the
 * parent font size scales the whole mark.
 */
export const PartnerWordmark: FC<PartnerWordmarkProps> = ({ name, className }): ReactNode => {
    const base = cn('inline-flex shrink-0 items-center whitespace-nowrap leading-none', className)

    switch (name) {
        case 'Northgate Provisions':
            return (
                <span className={base} role='img' aria-label={name}>
                    <span className='font-serif text-[1.25em] italic tracking-[-0.01em]'>Northgate</span>
                    <span className='ml-2 font-serif text-[0.62em] uppercase tracking-[0.22em]'>Provisions</span>
                </span>
            )
        case 'Halden & Rowe':
            return (
                <span className={base} role='img' aria-label={name}>
                    <span className='font-serif text-[0.92em] uppercase tracking-[0.26em]'>Halden</span>
                    <span className='mx-2 font-serif text-[1.2em] italic'>&amp;</span>
                    <span className='font-serif text-[0.92em] uppercase tracking-[0.26em]'>Rowe</span>
                </span>
            )
        case 'Kestrel Home':
            return (
                <span className={base} role='img' aria-label={name}>
                    <svg viewBox='0 0 20 20' aria-hidden='true' className='mr-2 size-[1em]'>
                        <path d='M2 15 L10 4 L18 15 Z' fill='currentColor' />
                    </svg>
                    <span className='font-heading text-[1.2em] font-extrabold lowercase tracking-[-0.06em]'>kestrel</span>
                    <span className='ml-1 font-heading text-[1.2em] font-light lowercase tracking-[-0.04em]'>home</span>
                </span>
            )
        case 'Volta Cycles':
            return (
                <span className={base} role='img' aria-label={name}>
                    <span className='font-mono text-[0.95em] font-medium uppercase tracking-[0.08em]'>Volta</span>
                    <span className='mx-1.5 font-mono text-[0.95em] opacity-50'>/</span>
                    <span className='font-mono text-[0.95em] uppercase tracking-[0.08em]'>Cycles</span>
                </span>
            )
        case 'Brightwater Foods':
            return (
                <span className={base} role='img' aria-label={name}>
                    <span aria-hidden='true' className='mr-2.5 inline-block size-[0.85em] rounded-full border-[0.14em] border-current' />
                    <span className='font-heading text-[1.12em] font-light tracking-[0.03em]'>Brightwater</span>
                    <span className='ml-1.5 font-heading text-[1.12em] font-semibold tracking-[-0.01em]'>Foods</span>
                </span>
            )
        case 'Ostrava Steel':
            return (
                <span className={base} role='img' aria-label={name}>
                    <span className='font-heading text-[1.08em] font-extrabold uppercase tracking-[-0.03em]'>Ostrava</span>
                    <span aria-hidden='true' className='mx-2 inline-block h-[0.9em] w-[0.18em] bg-current' />
                    <span className='font-heading text-[1.08em] font-medium uppercase tracking-[0.12em]'>Steel</span>
                </span>
            )
        case 'Pinecrest Outdoor':
            return (
                <span className={base} role='img' aria-label={name}>
                    <span className='font-serif text-[1.25em] font-bold tracking-[-0.02em]'>Pinecrest</span>
                    <span className='ml-1.5 self-start font-mono text-[0.5em] uppercase tracking-[0.18em]'>Outdoor</span>
                </span>
            )
        case 'Lumen Appliances':
            return (
                <span className={base} role='img' aria-label={name}>
                    <span className='font-heading text-[1.3em] font-semibold lowercase tracking-[-0.05em]'>lumen</span>
                    <span aria-hidden='true' className='mx-1.5 inline-block size-[0.32em] rounded-full bg-current' />
                    <span className='font-heading text-[0.8em] lowercase tracking-[0.02em] opacity-80'>appliances</span>
                </span>
            )
        case 'Marlow Textiles':
            return (
                <span className={base} role='img' aria-label={name}>
                    <span className='font-serif text-[1.25em] italic tracking-[-0.02em]'>Marlow</span>
                    <span className='ml-2 font-serif text-[1.25em] font-light italic tracking-[-0.02em] opacity-70'>Textiles</span>
                </span>
            )
        case 'Tandem Bikes':
            return (
                <span className={base} role='img' aria-label={name}>
                    <svg viewBox='0 0 30 20' aria-hidden='true' className='mr-2 h-[1em] w-[1.5em]'>
                        <circle cx='9' cy='10' r='6.5' fill='none' stroke='currentColor' strokeWidth='2.2' />
                        <circle cx='21' cy='10' r='6.5' fill='none' stroke='currentColor' strokeWidth='2.2' />
                    </svg>
                    <span className='font-heading text-[1.15em] font-bold italic tracking-[-0.04em]'>Tandem</span>
                    <span className='ml-1.5 font-heading text-[1.15em] font-normal italic tracking-[-0.03em]'>Bikes</span>
                </span>
            )
        default:
            return null
    }
}
